/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { FunctionComponent } from 'react';
import type { SchoolReportInterface, Teacher, TimeAssessmentFilterEnum } from '@whizz/api-model';
import type { SchoolReportType, SchoolTopicChartType, SchoolTopicTableType, SchoolViewType } from '../types';
import type { ManageMetricsType } from '../../forms/types';
import type { ChartIconType } from '../../components/charts/types';
import type { SchoolDownloadModalProps } from '../../global/modals/SchoolDownloadModal';
import type { ManageMetricsModalProps } from './modules/modals/ManageMetricsModal';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useI18n } from '@whizz/react-i18n';
import { GlobalDashboardLayout } from '../../layouts';
import { SchoolChartsView, SchoolTablesView } from './views';
import { useReportViewToggle } from '../../hooks/filters/useReportViewToggle';
import { useDateRangeSelector } from '../../hooks/filters/useDateRangeSelector';
import { useClassYearToggle } from '../../hooks/filters/useClassYearToggle';
import { Button, ButtonIcon } from '../../components';
import { GLOBAL_ICONS, QUERIES_IDS, ROUTES } from '../../util/vars';
import { Settings } from './modules';
import { useIdentity } from '@whizz/react-identity';
import { getDatesParams } from '../../helper/getDatesParams';
import { school_default_metrics_setting } from '../types';
import { mapSchoolReport } from '../../mapper/schoolReportMapper';
import { useSchoolFilterSelector } from '../../hooks/filters/useSchoolFilterSelector';
import { useQuery } from 'react-query';
import { model, repository } from '@whizz/api-client';
import { dateTimeToDateString } from '../../util/dates';
import { useModal } from '../../hooks/useModal';
import SchoolDownloadModal from '../../global/modals/SchoolDownloadModal';
import styled from 'styled-components';
import ManageMetricsModal from './modules/modals/ManageMetricsModal';
import { useLocalStorage } from 'react-use';
import NoData from '../../global/sections/NoData';
import { useSaveClass } from '../../hooks/filters/useClassSelector';
import { useHistory } from 'react-router-dom';

const SchoolReports: FunctionComponent = () => {
  const { locale, translate } = useI18n();
  const { me } = useIdentity();
  const history = useHistory();
  const teacherId = (me as Teacher).getId();
  const filteredIdsRef = useRef<number | null>(null);
  const  [ , setClassValue ] = useSaveClass(me?.username as string);

  const classesQuery = useQuery(
    [ QUERIES_IDS.CLASSES, teacherId ],
    () => repository.NewTeacherRepository.getClasses(teacherId!),
    {
      enabled: null != teacherId,
      placeholderData: [],
    },
  );

  const schoolId = null != classesQuery.data && 0 < classesQuery.data.length && null != classesQuery.data[0].schoolId
    ? classesQuery.data[0].schoolId
    : (
      (me as Teacher).getIsAdmin() ? (me as Teacher).getSchoolId() : null
    );

  const [ timeRangeSelector, timeRangeValue, timeRangeCustomValue ] = useDateRangeSelector({
    defaultValue: 'last_week',
    maxDate: new Date(),
    customOptions: [ 'last_x', 'last_week', 'since_x', 'current_acYear', 'since_last', 'custom' ],
  });
  const academicYearStartDateQuery = useQuery<DateTime>(
    [ QUERIES_IDS.ACADEMIC_YEAR_START_DATE, schoolId ],
    () => model.School.academicYearStartDate(schoolId!),
    {
      enabled: null != schoolId,
    },
  );
  const { startDateParam, endDateParam } = getDatesParams(timeRangeValue?.value ?? 'last_week', timeRangeCustomValue, academicYearStartDateQuery.data ?? null, me);

  const mapQueryData = useCallback((SchoolReportInterface) => mapSchoolReport(SchoolReportInterface), []);

  const isValidDate = timeRangeValue?.value !== 'custom' || (timeRangeValue?.value === 'custom' && DateTime.isDateTime(startDateParam) && DateTime.isDateTime(endDateParam));
  const queryIsEnabled = null != schoolId && isValidDate;

  const schoolReportQuery = useQuery<
  SchoolReportInterface,
  unknown, {
    schoolReport: SchoolReportType,
    chartTopic: SchoolTopicChartType,
    tableTopic: SchoolTopicTableType
  }>(
    [
      QUERIES_IDS.SCHOOL_REPORT,
      dateTimeToDateString(startDateParam),
      dateTimeToDateString(endDateParam),
    ],
    () => Promise.resolve( repository.SchoolReportRepository.one(
      locale,
      startDateParam as DateTime | TimeAssessmentFilterEnum,
      endDateParam ?? undefined,
    )),

    {
      enabled: queryIsEnabled,
      keepPreviousData: true,
      select: mapQueryData,
    },
  );

  const schoolReportQueryData = schoolReportQuery.data ?? mapSchoolReport(null);
  const [ viewToggle, viewValue ] = useReportViewToggle();
  const [ classYearToggle, classYearValue ] = useClassYearToggle();
  const data = classYearValue === 'class'
    ? schoolReportQueryData.schoolReport.classes.sort((a, b) => {
      if (a.fullName === b.fullName) {
        return 0;
      }

      return a.fullName > b.fullName ? 1 : -1;
    })
    : schoolReportQueryData.schoolReport.yearGroups;
  const [
    filterButton,
    filterValue,
    showOthersData,
    filterLabel,
    resetFilterValue,
    setShowOthersData,
    setLastSessionShowOthersData,
    setFilter,
  ] = useSchoolFilterSelector(
    data,
  );

  const activeChartTopicProgress = classYearValue === 'class'
    ? schoolReportQueryData.chartTopic.classes
    : schoolReportQueryData.chartTopic.yearGroups;

  const [ settings, setSettings ] = useLocalStorage<ManageMetricsType>(`school_report_settings_${me?.username}`, {
    default_settings: school_default_metrics_setting,
    additional_settings: [],
  });

  const handleSubmit = (values: ManageMetricsType)  => {
    setSettings(values);
  };

  const handleGroupClick = (groupId: number) => {
    if (filteredIdsRef.current === groupId) {
      resetFilterValue(false);
      return;
    }
    const findGroup = data.find((group) => group.id === groupId);
    if (findGroup) {
      setFilter(findGroup, false);
    }
  };

  const handleClassNameClick = (classId: number) => {
    const findClass = classesQuery.data?.find((tClass) => tClass.id === classId);
    if (findClass) {
      return () => {
        setClassValue({ label: findClass.name, value: findClass.id });
        history.push(ROUTES.reports);
      };
    }
  };

  const [ manageMetricsModal, toggleManageMetricsModal, setPropsForManageMetricsModal ] = useModal((props: ManageMetricsModalProps) => (

    <ManageMetricsModal {...props} />
  ), {
    title: translate('components.BUTTONS.MANAGE_METRICS'),
    icon: GLOBAL_ICONS.settings,
    isSchool: true,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    resetFilterValue(false);
  }, [ classYearValue ]);

  useEffect(() => {
    if (!filterValue) {
      setShowOthersData(true);
      setLastSessionShowOthersData(true);
    }
  }, [ filterValue ]);

  const isLoading = queryIsEnabled && (!schoolReportQuery.isFetched || schoolReportQuery.isFetching);

  const [ schoolDownloadModal, toggleSchoolDownloadModal, setDownloadModalProps, downloadModalIsOpen ] = useModal(
    (props: SchoolDownloadModalProps) => (<SchoolDownloadModal {...props} />),
  );

  const [ hideForPdf, SetHideForPdf ] = useState<boolean>(false);

  useEffect(() => {
    if (downloadModalIsOpen) return;

    SetHideForPdf(false);
  }, [ downloadModalIsOpen ]);

  useEffect(() => {
    filteredIdsRef.current = filterValue;
  }, [ filterValue ]);

  const hasData = () => {
    if (!data || data.length === 0) {
      return false;
    }
    const mathsAge = data.reduce((prev, current) => prev += current.chartsData?.mathsAge ?? 0, 0);
    return mathsAge > 0;
  };

  const noData = !hasData();
  return (
    <div id="reports-school-main-container">

      <GlobalDashboardLayout
        isLoading={isLoading}
        disableContainer
        title={translate('pages.SCHOOL_REPORTS.HEADER')}
        actionContent={classYearToggle}
        settings={(
          <Settings
            filterSelector={filterButton}
            timeRangeSelector={timeRangeSelector}
            viewTypeToggle={viewToggle}
            manageMetricsHandler={(
              <ManageMetricsButton
                color={'white'}
                icon={GLOBAL_ICONS.settings}
                size={'sm'}
                disabled={viewValue !== 'table'}
                onClick={() => {
                  setPropsForManageMetricsModal((prev) => (
                    {
                      ...prev,
                      settings: settings,
                      onSubmit: handleSubmit,
                      isSchool: true,
                    }));
                  toggleManageMetricsModal(true);
                }}
                data-cy="button-manage-metrics"
              >
                {translate('components.BUTTONS.MANAGE_METRICS')}
              </ManageMetricsButton>
            )}
            downloadHandler={(
              <ButtonIcon
                icon={GLOBAL_ICONS.download}
                color={'white'}
                isCircle
                onClick={() => {
                  setDownloadModalProps({
                    title: translate('pages.REPORTS.DOWNLOAD.MODAL.title'),
                    startDate: startDateParam,
                    endDate: endDateParam,
                  });
                  toggleSchoolDownloadModal(true);
                  SetHideForPdf(true);
                }}
                disabled={isLoading || noData}
              />
            )}
            hideDownloadButton={hideForPdf}
          />
        )}
        hideHeader={hideForPdf}
        fullWidthContent={viewValue === 'table'}
        sticky={viewValue === 'charts'}
      >
        {manageMetricsModal}
        {schoolDownloadModal}
        {noData ? <NoData alert /> : null }
        <div className={'space-y-4 md:space-y-6'} data-print-container>
          {'charts' === viewValue && (
            <SchoolChartsView
              schoolReport={schoolReportQueryData.schoolReport}
              chartTopicProgress={activeChartTopicProgress}
              filterLabel={filterLabel}
              filteredIds={null != filterValue ? [ filterValue ] : null}
              showOthersData={showOthersData}
              iconToUse={classYearValue as ChartIconType}
              dateRange={{
                startDate: startDateParam,
                endDate: endDateParam,
              }}
              onElementClick={handleGroupClick}
            />
          )}
          {'table' === viewValue && (
            <SchoolTablesView
              schoolReport={schoolReportQueryData.schoolReport}
              filteredIds={null != filterValue ? [ filterValue ] : null}
              classYearValue={classYearValue as SchoolViewType}
              showOthersData={showOthersData}
              metricsSettings={settings!}
              onNameClick={classYearValue === 'class' ? handleClassNameClick : undefined}
            />
          )}
        </div>
      </GlobalDashboardLayout>
    </div>
  );
};

const ManageMetricsButton = styled(Button)`
  border-radius: ${(props) => props.theme.radius.input};
`;

export default SchoolReports;
